import { Marker, StandaloneSearchBox } from '@react-google-maps/api';
import { createRef, useEffect, useRef, useState } from 'react';
import Api from '../../../api/Api';
import { IError } from '../../../api/ApiErrors';
import Color from '../../../data/Color';
import Flex from '../../../data/Flex';
import Size from '../../../data/Size';
import { dispatch, listen, unlisten } from '../../../dispatcher/Dispatcher';
import MapEvent from '../../../events/MapEvent';
import UIEvent from '../../../events/UIEvent';
import useMergeState from '../../../hooks/useMergeState';
import UIMap from '../../../ui/map/UIMap';

import { UIMapInfoBox } from '../../../ui/map/UIMapInfoBox';
import UIButton from '../../../ui/UIButton';
import UIFlex from '../../../ui/UIFlex';
import UIIcon from '../../../ui/UIIcon';
import { UILoadingState } from '../../../ui/UILoadingState';
import UITitle from '../../../ui/UITitle';
import NextFrame from '../../../util/NextFrame';
import { IProvisioningState } from './Provisioning';
import './MainViewMap.scss';
import { IGeoRequest } from '../../../api/ApiGoogleMaps';
import FormatUtil from '../../../util/FormatUtil';
import AppEvent from '../../../events/AppEvent';
import UILoadingIcon from '../../../ui/UILoadingIcon';
import { UILoadingBox } from '../../../ui/UILoadingBox';

import { ICDRData, handleSetICDRData } from '../../../data/CDRData';

export interface IMainViewMapProps {
    onMapClick?:()=>void;
    isFullscreen?:boolean;
    visible:boolean;
    
}


MainViewMap.ID = "mainMap";
MainViewMap.PATH = "map";

export function MainViewMap ($props: IMainViewMapProps) {

    

    const gmSearchBox = useRef<google.maps.places.SearchBox>();
    const uiMap = useRef(null);
    const map = useRef<google.maps.Map>();
    const updateManualTimeoutID = useRef(-1);
    

    const [selectedPlaceId, setSelectedPlaceId] = useState<string>();
    const [selectedPosition, setSelectedPosition] = useState<google.maps.LatLng | undefined>();
    
    const [showMapInfoBox, setShowMapInfoBox] = useState<boolean>(true);
    const [isLoadingGeocode, setLoadingGeocode] = useState<boolean>(false);
    const [cbn, setCBN] = useState<string>();

    let isInfoBoxOver = false;

    const _updateMapToEnteredAddress=($event:MapEvent)=>{

        let provisioningState:IProvisioningState = $event.data.state;

        let address:string = "";

        if(provisioningState.callerName.trim()){
            address = provisioningState.callerName.trim();
        }

        if(provisioningState.address.trim()){
            address += " "+provisioningState.address.trim();
            if(provisioningState.location_exact_comment.trim()){
                address+=" "+provisioningState.location_exact_comment.trim();
            }
        }
        if(provisioningState.city.trim()){
            if(provisioningState.state.trim()){
                address+=" "+provisioningState.city.trim()+", "+provisioningState.state.trim();
            }else{
                address+=" "+provisioningState.city.trim();
            }
        }else{
            if(provisioningState.state.trim()){
                address+=" "+provisioningState.state.trim();
            }
        }
        if(provisioningState.zipCode.trim()){
            address+=" "+provisioningState.zipCode.trim();
        }
        if(provisioningState.country.trim()){

            let country = FormatUtil.COUNTRIES_AS_UI_OPTIONS.find(($option)=>{
                return $option.value===provisioningState.country.trim();
            });

            if(country){
                if(country.label==="US"){
                    address+=" United States";
                }else{
                    address+=" "+country.label;
                }
            }
        }

        setLoadingGeocode(true);

        window.clearTimeout(updateManualTimeoutID.current);
        updateManualTimeoutID.current = window.setTimeout(()=>{

            let geoRequest:IGeoRequest = {
                makeNext:true,
                complete:($success,$result)=>{
                    
                    setLoadingGeocode(false);
                    if($success){
                        let result:google.maps.GeocoderResult | google.maps.places.PlaceResult = $result[0];
                        if(map && map.current && result.geometry?.viewport){
                            map.current.fitBounds(result.geometry.viewport);
                            if(result.geometry?.location){
                                setShowMapInfoBox(false);
                                setSelectedPosition(result.geometry.location);
                            }
                        }
                    }
                }
            };

            geoRequest.request={
                address:address,
            }

            Api.googleMap.debouncedGeocode(geoRequest);

        },2000);
    }




    useEffect(()=>{
        listen(MapEvent.UPDATE_MAP_TO_ADDRESS, _updateMapToEnteredAddress);

        return ()=>{

            if(updateManualTimeoutID.current!==-1){
                window.clearTimeout(updateManualTimeoutID.current);
            }

            unlisten(MapEvent.UPDATE_MAP_TO_ADDRESS, _updateMapToEnteredAddress);
        }
    },[]);

    useEffect(()=>{

        listen(AppEvent.GOT_CALLBACK_NUMBER, _onCallBackNumber);
        listen(AppEvent.VIEW_CDR_DETAILS,_onUseThisLocation);

        return ()=>{
            unlisten(AppEvent.GOT_CALLBACK_NUMBER, _onCallBackNumber);
            unlisten(AppEvent.VIEW_CDR_DETAILS,_onUseThisLocation);
        }
    },[]);

    const _onCallBackNumber = ($event:AppEvent)=>{
        
        let cbn:string = "";
        if($event.data.cbn){
            cbn = $event.data.cbn;
        }
        
        setCallBackNumber(cbn);
    }    

    const _onUseThisLocation=($event:AppEvent)=>{

        let cdrData:ICDRData = $event.data;     
        setCallBackNumber(cdrData.cbn);
    }


    const [callBackNumber, setCallBackNumber] = useState("");

    const _mapClick=($event:google.maps.MapMouseEvent)=>{

        if(!isInfoBoxOver){
            let tryIconEvent = $event as google.maps.IconMouseEvent;
            if(tryIconEvent.placeId){
                setSelectedPlaceId(tryIconEvent.placeId);
            }else{
                setSelectedPlaceId(undefined);
            }

            if(map && map.current && $event.latLng){
                map.current.panTo($event.latLng);
            }

            if($event.latLng){
                setShowMapInfoBox(true);
                setSelectedPosition($event.latLng);
            }
            if($props.onMapClick){
                $props.onMapClick();
            }
        }
    }

    const _infoBoxOver=()=>{
        isInfoBoxOver=true;
    }
    const _infoBoxOut=()=>{
        isInfoBoxOver=false;
    }

    let options:google.maps.MapOptions = {
        mapTypeControlOptions:{
            position:google.maps.ControlPosition.TOP_LEFT,
            style:google.maps.MapTypeControlStyle.DEFAULT,
        },
        fullscreenControl:false,
        fullscreenControlOptions:{
            position:google.maps.ControlPosition.BOTTOM_RIGHT,
        },
        clickableIcons:true,
        
    }



    return (
        <UIMap
            className={MainViewMap.ID+($props.visible?" visible":"")}
            ref={uiMap}                
            options={options}
            onMapLoaded={($map:google.maps.Map)=>{
                if($map){
                    map.current = $map;
                    Api.googleMap.setupMap($map);

                    $map.setCenter({lat:39.51073, lng:-98.4247});
                    $map.setZoom(4);
                }
            }}
            onUnmount={()=>{
                Api.googleMap.clearMap();
            }}
            onMapClick={_mapClick}
        >   
            <StandaloneSearchBox
                onLoad={($ref)=>{
                    gmSearchBox.current = $ref;
                }}
                onPlacesChanged={()=>{
                    if(gmSearchBox && gmSearchBox.current && map.current){
                        let places = gmSearchBox.current.getPlaces();
                        
                        if(places && places?.length>0){

                            let firstPlace = places[0];

                            if(firstPlace.geometry?.viewport){
                                map.current.fitBounds(firstPlace.geometry.viewport);

                                if(firstPlace.place_id){
                                    setSelectedPlaceId(firstPlace.place_id);
                                }else{
                                    setSelectedPlaceId(undefined);
                                }
                                
                                if(firstPlace.geometry.location){
                                    setShowMapInfoBox(true);
                                    setSelectedPosition(firstPlace.geometry.location);
                                }

                                if(places[0].geometry?.location){
                                    setSelectedPosition(places[0].geometry?.location);
                                }
    
                                //lets also plot this location

                            }
                        }
                    }
                }}
            >
                <input type="text" placeholder="Search Map" className='searchInput'/>
            </StandaloneSearchBox>   
    
            {selectedPosition && (
                <>
                    <Marker
                        key={selectedPosition.toString()}
                        position={selectedPosition}
                        draggable={true}
                        onDragStart={()=>{
                            setSelectedPlaceId(undefined);
                            setShowMapInfoBox(false);
                        }}
                        onDragEnd={(e: google.maps.MapMouseEvent)=>{
                            if(e.latLng){
                                setShowMapInfoBox(true);
                                setSelectedPosition(e.latLng);
                            }
                        }}
                        onClick={()=>{
                            setShowMapInfoBox(!showMapInfoBox);
                        }}
                    />
                    
                    {showMapInfoBox && (
                        <MainViewMapSelectedInfoBox
                            key={"infoBox"+selectedPosition.toString()}
                            position={selectedPosition}
                            placeId={selectedPlaceId}
                            onMouseOver={_infoBoxOver}
                            onMouseOut={_infoBoxOut}
                            onClose={()=>{
                                if(isInfoBoxOver){
                                    window.setTimeout(()=>{
                                        setShowMapInfoBox(false);
                                    },10);
                                }
                            }}
                            visible={$props.visible}
                            cbn={callBackNumber}
                        />
                    )}
                </>
            )}
            <UIButton 
                dropShadow 
                className='fullscreenBtn' 
                color={Color.WHITE} 
                label={$props.isFullscreen?"":""} 
                icon={$props.isFullscreen?UIIcon.FULLSCREEN_EXIT:UIIcon.FULLSCREEN} 
                noPadding={true} 
                isSquare={true} 
                iconFontSize={Size.s30} 
                size={Size.s40}
                onClick={()=>{
                    dispatch(MapEvent.TOGGLE_FULLSCREEN);
                }
            }/>
                {isLoadingGeocode && (
                    <UILoadingBox className='hasDropShadow'/>
                )}
        </UIMap>
    );
}


export interface IMainViewMapSelectedInfoBoxProps {
    position:google.maps.LatLng;
    placeId?:string;
    onMouseOver?:()=>void;
    onMouseOut?:()=>void;
    onClose?:()=>void;
    visible:boolean;
    cbn?:string;
}

export interface IGeocodeResult{
    results:(google.maps.GeocoderResult | google.maps.places.PlaceResult)[];
    title:string;
    desc1:string;
    desc2:string;
    street_number:string;
    route:string;
    city:string;
    state:string;
    postalCode:string;
    county:string;
    country:string;
    cbn:string;
}

export interface IMainViewMapSelectedInfoBoxState{
    loading:boolean;
    error?:IError;
    data?:IGeocodeResult;
}


const parseGeocoderResults=($results:(google.maps.GeocoderResult | google.maps.places.PlaceResult)[])=>{

    

    let title = "";
    let street_number:string = "";
    let route:string = "";
    let city:string = "";
    let state:string = "";
    let postalCode:string = "";
    let county:string = "";
    let country:string = "";

    let anyFormattedAddress:string = "";
    let cbn:string = "";

    $results.forEach(($result)=>{
        //look for title
        if($result.place_id){
            let placeResult = $result as google.maps.places.PlaceResult;
            if(placeResult.name){
                if(title===""){
                    title = placeResult.name;
                }
            }
        }

        if(anyFormattedAddress==="" && $result.formatted_address){
            anyFormattedAddress = $result.formatted_address;
        }
        
        if($result.address_components){
            //loop through the levels until we find 
            $result.address_components.forEach(($addressPiece)=>{
                
                let type1 = $addressPiece.types[0];
                let type2 = "";
                if($addressPiece.types.length>1){
                    type2 = $addressPiece.types[1];
                }
                switch(type1){
                    case "street_number":
                        if(street_number===""){
                            street_number = $addressPiece.long_name;
                        }
                    break;
                    case "route": //street
                        if(route===""){
                            route = $addressPiece.short_name;
                        }
                    break;
                    case "locality"://city
                        if(city===""){
                            city = $addressPiece.short_name;
                        }
                    break;
                    case "administrative_area_level_2"://county
                        if(county===""){
                            county = $addressPiece.short_name;    
                        }
                    break;
                    case "administrative_area_level_1"://state
                        if(state===""){
                            state = $addressPiece.short_name;
                        }
                    break;
                    case "country"://country
                        if(country===""){
                            country = $addressPiece.short_name;
                        }
                    break;
                    case "postal_code"://zip
                        if(postalCode===""){
                            postalCode = $addressPiece.short_name;
                        }
                    break;
                }    
            });
        }


    });


    let desc1 = street_number+" "+route;

    let desc2 = city?city:county;

    if(state){
        desc2+=", "+state;
    }
    if(postalCode){
        desc2+=" "+postalCode;
    }

    if(desc2.trim()==="," || desc2.trim()===""){
        desc2 = anyFormattedAddress;
    }


    let parsed = {
        results:$results,
        desc1,
        desc2,
        title,
        street_number,
        route,
        city,
        state,
        postalCode,
        county,
        country,
        cbn,
    }

    return parsed;
}


export function MainViewMapSelectedInfoBox($props:IMainViewMapSelectedInfoBoxProps) {

    const [mState, setMergeState] = useMergeState({loading:true} as IMainViewMapSelectedInfoBoxState);

    const infoBoxState:IMainViewMapSelectedInfoBoxState = mState;

    const lastPos = useRef("");

    let strPosition = $props.position.toString();

    let placeId = $props.placeId;

    let visible = $props.visible;

    useEffect(()=>{


        if($props.position && lastPos.current!==strPosition){
            lastPos.current = strPosition;//use to make sure this only happens once becuase... useEffect doesn't care?

            setMergeState({data:undefined, loading:true, error:undefined});

            Api.googleMap.debouncedGeocode({
                request:{location:$props.position},
                makeNext:true,
                placeId:placeId,
                complete:($success,$results)=>{

                    setMergeState({loading:false});

                    NextFrame(()=>{
                        if($success){
                            
                            

                            let parsedData = parseGeocoderResults($results);
                            
                            setMergeState({data:parsedData});
    
                        }else{
                            setMergeState({error:$results});
                        }
                    });
                }
            });
        }

    },[strPosition, placeId, $props.position,setMergeState]);
    //
    return (
        <>
        {visible && (
        <UIMapInfoBox 
            onMouseOver={$props.onMouseOver} 
            onMouseOut={$props.onMouseOut} 
            width={200} 
            position={$props.position}
        >
            <UILoadingState loading={infoBoxState.loading} error={infoBoxState.error}>
                <UIFlex flex={{direction:Flex.COLUMN}}>
                    {infoBoxState.data && (
                        <>
                        {infoBoxState.data.title && (
                            <UITitle title={infoBoxState.data.title}/>
                        )}
                        <UIFlex className='desc1'>{infoBoxState.data.desc1}</UIFlex>
    
                        {infoBoxState.data.desc2 && (
                            <UIFlex className='desc2'>{infoBoxState.data.desc2}</UIFlex>
                        )}
                        <UIButton dropShadow size={Size.s30} fontSize={Size.s14} label="Use this Location" onClick={()=>{
                            if(infoBoxState.data){

                                infoBoxState.data.cbn = $props.cbn?$props.cbn:"";
                                dispatch(MapEvent.USE_CHOSEN_LOCATION,infoBoxState.data);
                            }
                        }}/>
                        </>
                    )}
                    
                    {$props.onClose && (
                        <UIButton className='closeBtn' size={Size.s20} color={Color.TRANSPARENT} isSquare noPadding icon={UIIcon.CLOSE} onClick={()=>{
                            $props.onClose!();
                        }}/>
                    )}
                </UIFlex>
            </UILoadingState>
        </UIMapInfoBox>
        )}
        </>
    );
}
